import { useAtomValue } from "jotai";
import pym from "pym.js";
import { useEffect, useRef, useState } from "react";

import { config } from "shared/data";
import { accountAtom } from "shared/lib/account";

import { pathname as newsletterPathname } from "./index";

let pymChild: pym.Child;

export function getPymChild(): pym.Child {
  pymChild ??= new pym.Child();
  return pymChild;
}

export function onMessage<T = string>(type: string, callback: (payload: T) => void): void {
  getPymChild().onMessage(type, (raw: string) => {
    callback(raw as T);
  });
}

export function sendMessage(type: string, data?: string): void {
  getPymChild().sendMessage(type, data ?? "");
}

export const useIframeCommunicator = (enabled = false) => {
  const cleanupReference = useRef<Array<() => void>>([]);

  useEffect(() => {
    if (!enabled) return;

    const newsletterIframe = getPymChild();

    const sendHeightUpdate = () => {
      try {
        newsletterIframe.sendHeight();
      } catch (error) {
        console.error("Failed to send height update:", error);
      }
    };

    const sendReadyMessage = () => {
      try {
        newsletterIframe.sendMessage(
          "NEWSLETTER_READY",
          JSON.stringify({
            status: "ready",
            timestamp: Date.now(),
          }),
        );
        sendHeightUpdate();
      } catch (error) {
        console.error("Failed to send newsletter ready message:", error);
      }
    };

    const handleContentLoaded = () => {
      setTimeout(() => {
        sendHeightUpdate();
        newsletterIframe.sendMessage("CONTENT_LOADED", "");
      }, 100);
    };

    const handleAllAssetsLoaded = () => {
      setTimeout(sendHeightUpdate, 200);
    };

    const rafId = requestAnimationFrame(sendReadyMessage);
    cleanupReference.current.push(() => cancelAnimationFrame(rafId));

    window.addEventListener("load", handleAllAssetsLoaded);
    document.addEventListener("DOMContentLoaded", handleContentLoaded);

    cleanupReference.current.push(() => {
      window.removeEventListener("load", handleAllAssetsLoaded);
      document.removeEventListener("DOMContentLoaded", handleContentLoaded);
    });

    let mutationTimeout: NodeJS.Timeout;
    const observer = new MutationObserver(() => {
      clearTimeout(mutationTimeout);
      mutationTimeout = setTimeout(sendHeightUpdate, 100);
    });

    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    cleanupReference.current.push(() => {
      clearTimeout(mutationTimeout);
      observer.disconnect();
    });

    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        sendHeightUpdate();
      });
      resizeObserver.observe(document.body);
      cleanupReference.current.push(() => resizeObserver?.disconnect());
    }

    pymChild.onMessage("pym:request-height", () => {
      sendHeightUpdate();
    });

    const handleBeforeUnload = () => {
      cleanupReference.current.forEach(cleanupFunction => {
        try {
          cleanupFunction();
        } catch (error) {
          console.error("Error during cleanup:", error);
        }
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    cleanupReference.current.push(() =>
      window.removeEventListener("beforeunload", handleBeforeUnload),
    );

    const cleanup = cleanupReference.current;

    return () => {
      cleanup.forEach(cleanupFunction => {
        try {
          cleanupFunction();
        } catch (error) {
          console.error("Error during component cleanup:", error);
        }
      });
    };
  }, [enabled]);
};

export const useNewsletterUrl = () => {
  const { isLoggedIn } = useAtomValue(accountAtom);
  const [newsletterUrl, setNewsletterUrl] = useState(newsletterPathname);

  useEffect(() => {
    if (isLoggedIn === true) {
      setNewsletterUrl(`${config.account.scmpAccountHost}/manage/newsletter`);
    }
  }, [isLoggedIn]);

  return {
    newsletterUrl,
  };
};
